import { useCallback } from 'react';
import {
  useAddKmEvalNoteSet,
  useCreateKmEval,
  useDeleteKmEval,
  useMoveKmEval,
  useRemoveKmEvalNoteSet,
  useUpdateKmAssessment,
  useUpdateKmEval,
  type KmAssessmentResponse,
  type KmEvalResponse,
  type KmNoteSetResponse,
  type KmOrderPosition,
} from '@/entities/expert-report-km';
import type { PredicateInput } from '@/entities/expert-report';
import type { PredicateValue } from '@/entities/predicate';
import type { KmAssessmentFormValues, KmEditableEval } from './types';

/** Наборы знаний, которые нужно привязать и отвязать, чтобы состав совпал с формой */
const diffNoteSets = (current: KmNoteSetResponse[], next: KmNoteSetResponse[]) => {
  const currentIds = new Set(current.map((item) => item.id));
  const nextIds = new Set(next.map((item) => item.id));
  return {
    added: next.filter((item) => !currentIds.has(item.id)).map((item) => item.id),
    removed: current.filter((item) => !nextIds.has(item.id)).map((item) => item.id),
  };
};

/** Условие из формы в виде запроса; пустая запись — условия нет */
const toEvalPredicate = (value: PredicateValue | undefined): PredicateInput | undefined =>
  value?.dsl ? { dsl: value.dsl, dslHuman: value.dslHuman || null } : undefined;

const isPredicateChanged = (edited: PredicateValue | undefined, original: KmEvalResponse['predicate']) => {
  const next = toEvalPredicate(edited);
  if (!next || !original?.dsl) return Boolean(next) !== Boolean(original?.dsl);
  return next.dsl !== original.dsl || (next.dslHuman ?? '') !== (original.dslHuman ?? '');
};

const isEvalChanged = (edited: KmEditableEval, original: KmEvalResponse) =>
  edited.name !== original.name ||
  edited.text !== original.text ||
  isPredicateChanged(edited.predicate, original.predicate);

/** Итог сохранения карточки единицы оценивания */
export type KmAssessmentSaveResult = {
  /** Причина, по которой сервер не смог определить шкалу единицы; null — определил */
  pointsError: string | null;
  /** Хотя бы одну правку сервер отклонил, остальные при этом сохранены */
  hasRejectedChanges: boolean;
};

/**
 * Сохранение карточки единицы оценивания: название единицы, состав и порядок её определений
 * оценки, их условия применимости и привязанные к ним наборы знаний. Каждый элемент структуры правится своей операцией
 * раздела, поэтому карточка складывает разницу с сохранённым состоянием и шлёт только её.
 *
 * Правки независимы: отклонённая сервером операция не отменяет остальные — иначе одна
 * неудача (например, привязка набора знаний) уносила бы с собой все последующие правки.
 * О самой причине отказа сообщает общий обработчик ошибок.
 */
export const useSaveKmAssessment = () => {
  const { mutateAsync: updateAssessment } = useUpdateKmAssessment();
  const { mutateAsync: createEval } = useCreateKmEval();
  const { mutateAsync: updateEval } = useUpdateKmEval();
  const { mutateAsync: moveEval } = useMoveKmEval();
  const { mutateAsync: deleteEval } = useDeleteKmEval();
  const { mutateAsync: addNoteSet } = useAddKmEvalNoteSet();
  const { mutateAsync: removeNoteSet } = useRemoveKmEvalNoteSet();

  return useCallback(
    async (assessment: KmAssessmentResponse, values: KmAssessmentFormValues): Promise<KmAssessmentSaveResult> => {
      const originalById = new Map(assessment.evals.map((item) => [item.id, item]));
      const keptIds = new Set(values.evals.map((item) => item.id).filter((id): id is number => typeof id === 'number'));
      let pointsError: string | null = null;
      let hasRejectedChanges = false;

      // Отказ одной правки не должен отменять остальные: он только помечается и не прерывает сохранение
      const apply = async <T>(operation: () => Promise<T>): Promise<T | undefined> => {
        try {
          return await operation();
        } catch {
          hasRejectedChanges = true;
          return undefined;
        }
      };

      // Убранные из формы определения оценки
      for (const original of assessment.evals) {
        if (!keptIds.has(original.id)) {
          await apply(() => deleteEval(original.id));
        }
      }

      for (const [index, edited] of values.evals.entries()) {
        const orderPosition: KmOrderPosition = index + 1;

        if (edited.id === undefined) {
          const created = await apply(() =>
            createEval({
              assessmentId: assessment.id,
              name: edited.name.trim(),
              text: edited.text,
              orderPosition,
              predicate: toEvalPredicate(edited.predicate),
            }),
          );
          // Наборы знаний привязываются к созданному определению, поэтому без него их пропускаем
          if (!created) continue;

          pointsError = created.pointsError ?? pointsError;
          for (const noteSetId of edited.noteSets.map((item) => item.id)) {
            await apply(() => addNoteSet({ evalId: created.id, noteSetId }));
          }
          continue;
        }

        const evalId = edited.id;
        const original = originalById.get(evalId);
        if (!original) continue;

        if (isEvalChanged(edited, original)) {
          const updated = await apply(() =>
            updateEval({
              evalId,
              body: {
                name: edited.name.trim(),
                text: edited.text,
                // Снятое в форме условие уходит явным null: без поля сервер оставил бы прежнее
                predicate: toEvalPredicate(edited.predicate) ?? null,
              },
            }),
          );
          pointsError = updated?.pointsError ?? pointsError;
        }
        if (original.orderPosition !== orderPosition) {
          await apply(() => moveEval({ evalId, body: { orderPosition } }));
        }

        const { added, removed } = diffNoteSets(original.noteSets, edited.noteSets);
        for (const noteSetId of added) {
          await apply(() => addNoteSet({ evalId, noteSetId }));
        }
        for (const noteSetId of removed) {
          await apply(() => removeNoteSet({ evalId, noteSetId }));
        }
      }

      const name = values.name.trim();
      if (name && name !== assessment.name) {
        await apply(() => updateAssessment({ assessmentId: assessment.id, body: { name } }));
      }

      return { pointsError, hasRejectedChanges };
    },
    [addNoteSet, createEval, deleteEval, moveEval, removeNoteSet, updateAssessment, updateEval],
  );
};
